import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { ColorType } from '@adornis/chemistry/elements/theming/coloring-system.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { AdornisTodo } from '@adornis/todo/db/AdornisTodo.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { allTinyMCEPlugins } from '@adornis/writer/x-tinymce.js';
import type { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { map } from 'rxjs';
import { ChromiumDialog } from '../../design/components/cr-dialog.js';
import { ChromiumSnackbar } from '../../design/components/cr-snackbar.js';
import '../semantics/chancen-component-semantic-user-picker.js';
import { taggerTinyMCEPlugin } from '../tinyPlugins/chancen-tiny-plugin-entity-tagger.js';

@customElement('chancen-dialog-add-todo')
export class ChancenDialogAddTodo extends ChromiumDialog<void> {
  @state() protected readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  static override get element_name() {
    return 'chancen-dialog-add-todo';
  }

  override styles() {
    return [...super.styles(), {}];
  }

  @state() private readonly _todo = new RXController(
    this,
    this._currentUser.observable.pipe(map(user => new AdornisTodo({ assigneeID: user?._id, assignee: user }))),
  );

  override heading = 'Todo erstellen';
  override background: ColorType = this.colors.neutral.shade(50);

  @state() private readonly _assigneeError = new RXController<Maybe<string>>(this, undefined);

  private readonly _form = new FormController(this, this._todo.observable, {
    success: async todo => {
      void ChromiumDialog.guard(
        async () => {
          await todo.create();
        },
        {
          success: async () => {
            await ChromiumSnackbar.show('Todo erfolgreich angelegt');
            void this.close();
          },
        },
      );
    },
    error: err => {
      if (err.message.includes('Key assigneeID is required')) {
        this._assigneeError.next('Die zuständige Person muss angegeben werden!');
      }
      void ChromiumSnackbar.show('Fehler beim speichern!');
    },
  });

  protected override content(): Renderable {
    return html`
      <cr-flex space="sm">
        ${permissionGuard(
          ChancenPermission.global.ASSIGN_TODOS_TO_OTHERS,
          () => html`
            <cr-card space="sm">
              <cr-h4>Zuständigkeit</cr-h4>

              <chancen-component-semantic-user-picker
                .error=${this._assigneeError.value}
                @value-picked=${(e: ValueEvent<Maybe<ChancenwerkUser>>) => {
                  if (!this._form.document) return;
                  this._form.document.assigneeID = e.detail.value?._id ?? null;
                  this._form.document.assignee = e.detail.value;
                  this._assigneeError.next(undefined);
                  this.requestUpdate();
                }}
                @cleared=${() => {
                  if (!this._form.document) return;
                  this._form.document.assigneeID = null;
                  this._form.document.assignee = null;
                  this._assigneeError.next('Die zuständige Person muss angegeben werden!');
                }}
                .value=${this._form.document?.assignee}
              ></chancen-component-semantic-user-picker>
            </cr-card>
          `,
          { forbiddenTemplate: nothing, context: ANY_CONTEXT },
        )}

        <cr-flex space="sm" padding="sm" ${css({ borderRadius: '15px', backgroundColor: 'white' })}>
          <cr-h4>Todo</cr-h4>
          <x-tinymce
            ${this._form.field('name')}
            .customPlugins=${[taggerTinyMCEPlugin]}
            .editorOptions=${{
              convert_urls: false,
              plugins: [...allTinyMCEPlugins, 'cwTagger'],
              toolbar:
                'undo redo | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent lineheight | numlist bullist | forecolor backcolor removeformat | charmap emoticons | link | ltr rtl',
            }}
          ></x-tinymce>
        </cr-flex>
      </cr-flex>
    `;
  }

  protected override footerContent(): Renderable {
    return html`
      <cr-button
        @click=${() => {
          void this._form.validate();
        }}
      >
        Todo erstellen
      </cr-button>
    `;
  }

  public override getContentWrapper(options: DialogOptions) {
    const wrapperElem = super.getContentWrapper(options as Required<DialogOptions>);

    if (!options.draggable) {
      wrapperElem.style.setProperty('y-overflow', 'hidden');
      wrapperElem.style.setProperty('min-height', '98vh');
      wrapperElem.style.setProperty('max-height', '98vh');
    }
    return wrapperElem;
  }
}
